/**
 * Currency Service
 *
 * Service for fetching currency information and exchange rates.
 * Provides conversion calculations and caching.
 */

import { apiClient } from './api-client';

// Types
export interface Currency {
  code: string;
  name: string;
  symbol: string;
  is_active: boolean;
  is_default: boolean;
}

export interface ExchangeRatesData {
  defaultCurrency: string;
  rates: Array<{
    fromCurrency: string;
    toCurrency: string;
    rate: number;
    updatedAt: string;
  }>;
}

export interface ConversionResult {
  amount: number;
  fromCurrency: string;
  toCurrency: string;
  convertedAmount: number;
  rate: number;
  inverse: boolean;
}

// Cache for exchange rates
let exchangeRatesCache: ExchangeRatesData | null = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get all active currencies
 */
export async function getActiveCurrencies(): Promise<Currency[]> {
  const response = await apiClient.get<Currency[]>('/api/currencies');
  return response.data;
}

/**
 * Get the default currency
 */
export async function getDefaultCurrency(): Promise<Currency> {
  const response = await apiClient.get<Currency>('/api/currencies/default');
  return response.data;
}

/**
 * Get exchange rates with caching
 */
export async function getExchangeRates(forceRefresh = false): Promise<ExchangeRatesData> {
  const now = Date.now();

  // Return cached data if still valid
  if (!forceRefresh && exchangeRatesCache && (now - cacheTimestamp < CACHE_DURATION)) {
    return exchangeRatesCache;
  }

  const response = await apiClient.get<ExchangeRatesData>('/api/currencies/rates');
  exchangeRatesCache = response.data;
  cacheTimestamp = now;

  return response.data;
}

/**
 * Convert amount between currencies using the API
 */
export async function convertCurrency(
  amount: number,
  fromCurrency: string,
  toCurrency?: string
): Promise<ConversionResult> {
  const params: Record<string, string> = {
    amount: amount.toString(),
    from: fromCurrency
  };

  if (toCurrency) {
    params.to = toCurrency;
  }

  const response = await apiClient.get<ConversionResult>('/api/currencies/convert', { params });
  return response.data;
}

/**
 * Convert amount to default currency
 */
export async function convertToDefaultCurrency(
  amount: number,
  fromCurrency: string
): Promise<ConversionResult> {
  return convertCurrency(amount, fromCurrency);
}

/**
 * Format currency amount with proper symbol and locale
 */
export function formatCurrencyAmount(
  amount: number,
  currency: string,
  locale = 'en-US'
): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

/**
 * Get exchange rate between two currencies from cached data
 * Returns null if no rate available or cache is stale
 */
export function getCachedExchangeRate(
  fromCurrency: string,
  toCurrency: string
): number | null {
  if (!exchangeRatesCache) return null;

  // Check cache validity
  if (Date.now() - cacheTimestamp > CACHE_DURATION) return null;

  // Same currency
  if (fromCurrency === toCurrency) return 1;

  // Find direct rate
  const directRate = exchangeRatesCache.rates.find(
    r => r.fromCurrency === fromCurrency && r.toCurrency === toCurrency
  );
  if (directRate) return directRate.rate;

  // Find inverse rate
  const inverseRate = exchangeRatesCache.rates.find(
    r => r.fromCurrency === toCurrency && r.toCurrency === fromCurrency
  );
  if (inverseRate) return 1 / inverseRate.rate;

  return null;
}

/**
 * Perform local conversion using cached rates
 * Falls back to API if cache is stale or rate not found
 */
export async function convertCurrencyLocal(
  amount: number,
  fromCurrency: string,
  toCurrency: string
): Promise<ConversionResult> {
  // Try to use cached rate first
  const cachedRate = getCachedExchangeRate(fromCurrency, toCurrency);

  if (cachedRate !== null) {
    return {
      amount,
      fromCurrency,
      toCurrency,
      convertedAmount: amount * cachedRate,
      rate: cachedRate,
      inverse: false
    };
  }

  // Fall back to API
  return convertCurrency(amount, fromCurrency, toCurrency);
}